import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  PlusIcon,
  CalendarIcon,
  CheckCircleIcon,
  UserIcon,
  EyeIcon,
  InformationCircleIcon,
  UserGroupIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import { updateUrlWithException, dispatchWithToast } from "../../../../components/Utils";
import { debounce } from "lodash";
import { getUsersAsync, createUserAsync } from "../../../../slices/user";
import { UsersTable } from "../../../../components/dashboard/company/users/users-table";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { PMField } from "../../../../components/utils/PMField";
import PMSwitch from "../../../../components/utils/PMSwitch";
import ModernSupplierSelector from "../../../../components/utils/Selector/ModernSupplierSelector";
import { checkEmail, resendInvitation } from "../../../../api/user";
import { toast } from "react-hot-toast";

/**
 * Company users list.
 * @param {object} props
 * @param {boolean} [props.employeesOnly] When true (HR → Employees), reuse the same
 *   list/API but always filter to users with an active Employee row in the company.
 */
export const CompanyUsersList = ({ employeesOnly = false } = {}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const usersState = useSelector((store) => {
    return store.users;
  });

  const { isLoading } = usersState;
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchParams] = useSearchParams();
  const [emailSearchValue, setEmailSearchValue] = useState("");
  const [emailCheckResult, setEmailCheckResult] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  // Innleid (hired) supplier picker for the create form.
  const [supplierSelectorOpen, setSupplierSelectorOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [resendingInvitation, setResendingInvitation] = useState(false);
  // Mobile-only filter dialog disclosure (desktop keeps the inline filter card).
  const [filterOpen, setFilterOpen] = useState(false);

  const pageTitle = employeesOnly
    ? t("hr.employees.page_title")
    : t("company.users.page_title");
  const heroKicker = employeesOnly
    ? t("hr.employees.hero.kicker")
    : t("company.users.hero.kicker");
  const heroSubtitle = employeesOnly
    ? t("hr.employees.hero.subtitle")
    : t("company.users.hero.subtitle");

  const [controller, setController] = useState({
    filters: employeesOnly ? [{ name: "employee", value: true }] : [],
    per_page: 25,
    page: searchParams.get("page") ? searchParams.get("page") : 1,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "asc",
    sortBy: searchParams.get("sortBy") ? searchParams.get("sortBy") : "employee_number",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    showInDropdown: searchParams.get("showInDropdown") === "true",
    active: searchParams.get("active") !== null ? searchParams.get("active") === "true" : true, // Default to true (only active users)
  });

  // Update all params from controller except filters (dont't touch filters)
  useEffect(() => {
    updateUrlWithException(
      // These will be updated
      {
        page: controller.page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        showInDropdown: controller.showInDropdown,
        active: controller.active,
      },
      // This will be preserved
      "filters"
    );
  }, [controller]);

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleFiltersChange = (filters) => {
    // Convert ModuleFilter format to backend format
    const backendFilters = [];

    // Handle active filter - send as query parameter, not as filter
    // filters.active will be handled separately below

    // HR Employees view always scopes to active employment rows.
    if (employeesOnly || filters.employee) {
      backendFilters.push({
        name: "employee",
        value: true,
      });
    }

    if (filters.showInDropdown) {
      backendFilters.push({
        name: "showInDropdown",
        value: true,
      });
    }

    // Handle driver filter (if still using status)
    if (filters.statuses && filters.statuses.includes("driver")) {
      backendFilters.push({
        name: "driver",
        value: true,
      });
    }

    // Handle role filters (if still using status)
    if (filters.statuses && filters.statuses.length > 0) {
      filters.statuses.forEach((status) => {
        if (status !== "employee" && status !== "driver") {
          backendFilters.push({
            name: "role",
            value: status,
          });
        }
      });
    }

    // When searching, remove active filter to show all matching results
    // This provides better UX - users expect to find what they search for
    // Note: Other filters (like showInDropdown) are kept as they may be context-specific
    const hasSearchQuery = filters.query !== undefined && filters.query.trim() !== "";
    const activeFilter = hasSearchQuery 
      ? undefined // Remove active filter when searching
      : (filters.active !== undefined ? filters.active : controller.active);

    setController({
      ...controller,
      page: 1,
      query: filters.query !== undefined ? filters.query : controller.query,
      filters: backendFilters,
      active: activeFilter, // Update active parameter (removed when searching)
    });
  };

  const [userDialogOpen, setUserDialogOpen] = useState(false);

  // Define custom checkbox button filters for users.
  // In HR Employees mode the employee filter is locked on (not shown as a toggle).
  const customCheckboxButtonFilters = [
    {
      key: "active",
      label: t("company.users.filter.active"),
      icon: CheckCircleIcon,
      defaultValue: true,
    },
    ...(!employeesOnly
      ? [
          {
            key: "employee",
            label: t("company.users.filter.employee"),
            icon: UserIcon,
            defaultValue: true,
          },
        ]
      : []),
    {
      key: "showInDropdown",
      label: t("company.users.filter.show_in_dropdown"),
      icon: EyeIcon,
      defaultValue: false,
    },
  ];

  const debouncedDispatchAction = useCallback(
    debounce((controller) => {
      dispatch(getUsersAsync(controller));
    }, 250),
    [dispatch]
  );

  const debouncedCheckEmail = useCallback(
    debounce(async (email) => {
      if (!email || email.trim() === "") {
        setEmailCheckResult(null);
        return;
      }

      setCheckingEmail(true);
      try {
        const result = await checkEmail(email);
        setEmailCheckResult(result);
      } catch (error) {
        console.error("Error checking email:", error);
        setEmailCheckResult(null);
      } finally {
        setCheckingEmail(false);
      }
    }, 500),
    []
  );

  const handleEmailSearchChange = (value, formik) => {
    setEmailSearchValue(value);
    formik.setFieldValue("email", value);
    debouncedCheckEmail(value);
  };

  const handleResendInvitation = async () => {
    const userId = emailCheckResult?.user?.id;
    const normalizedEmail = emailSearchValue?.trim();

    if (!userId || !normalizedEmail) return;

    setResendingInvitation(true);
    try {
      const response = await resendInvitation(userId);
      if (response?.errors?.length) {
        toast.error(response.errors.join(", "));
        return;
      }

      toast.success(response?.message || t("company.users.invitation.resend_success"));

      // Refresh email check state so the UI reflects updated invitation status.
      const refreshedResult = await checkEmail(normalizedEmail);
      setEmailCheckResult(refreshedResult);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errors?.join(", ") ||
        t("company.users.invitation.resend_error");
      toast.error(errorMessage);
    } finally {
      setResendingInvitation(false);
    }
  };

  useEffect(() => {
    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, debouncedDispatchAction]);

  useEffect(() => {
    document.title = `${pageTitle} | Promang`;
  }, [pageTitle]);

  const openUsersCalendar = () => {
    const today = new Date().toISOString().split("T")[0];
    if (selectedItems.length > 0) {
      const userIds = selectedItems.map((user) => user.id).join(",");
      window.open(
        `/dashboard/calendar?date=${today}&view=day&users=${userIds}&showWeekNumbers=false`,
        "_blank"
      );
    } else {
      window.open(
        `/dashboard/calendar?date=${today}&view=day&showWeekNumbers=false`,
        "_blank"
      );
    }
  };

  // Convert backend filters to ModuleFilter format
  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query,
      statuses: [],
      employee: employeesOnly,
      active: controller.active !== undefined ? controller.active : true, // Read from controller.active, default to true
      showInDropdown: false,
    };

    if (controller.filters && controller.filters.length > 0) {
      controller.filters.forEach((filter) => {
        switch (filter.name) {
          case "employee":
            if (filter.value === true) {
              initialFilters.employee = true;
            }
            break;
          case "driver":
            if (filter.value === true) {
              initialFilters.statuses.push("driver");
            }
            break;
          case "showInDropdown":
            if (filter.value === true) {
              initialFilters.showInDropdown = true;
            }
            break;
          case "role":
            initialFilters.statuses.push(filter.value);
            break;
        }
      });
    }

    return initialFilters;
  };

  const totalUsers = usersState.totalCount ?? 0;
  const onPageCount = usersState.currentCount ?? usersState.data?.length ?? 0;
  // Drives the mobile filter button's active-state dot (derived, not stored).
  const hasActiveFilters =
    (controller.filters?.length ?? 0) > 0 || Boolean(controller.query);

  return (
    <>
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
          <section className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)] sm:mb-6">
            <div className="flex items-center gap-3 px-3 py-3 sm:px-5 sm:py-4 lg:justify-between">
              <div className="flex min-w-0 flex-1 items-center gap-2.5 lg:items-start">
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
                  <UserGroupIcon className="h-[18px] w-[18px]" />
                </span>
                <div className="min-w-0">
                  <span className="hidden items-center rounded-full bg-[#EAF3FD] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#1463C7] sm:inline-flex">
                    {heroKicker}
                  </span>
                  <h1 className="truncate text-base font-bold tracking-tight text-slate-900 sm:mt-1.5 sm:text-xl sm:font-extrabold">
                    {pageTitle}
                  </h1>
                  <p className="mt-0.5 hidden max-w-2xl text-[13px] text-slate-500 sm:block">
                    {heroSubtitle}
                  </p>
                </div>
              </div>

              <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={() => setFilterOpen(true)}
                  aria-label={t("company.users.filter_title")}
                  title={t("company.users.filter_title")}
                  className="relative inline-flex items-center justify-center rounded-[10px] border border-slate-200 bg-white p-2 text-slate-600 transition hover:bg-slate-50 sm:hidden"
                >
                  <FunnelIcon className="h-4 w-4" />
                  {hasActiveFilters ? (
                    <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[#1463C7]" />
                  ) : null}
                </button>
                <button
                  type="button"
                  onClick={openUsersCalendar}
                  aria-label={t("company.users.calendar")}
                  title={t("company.users.calendar")}
                  className="inline-flex items-center justify-center gap-1.5 rounded-[10px] border border-slate-200 bg-white p-2 text-[13.5px] font-semibold text-slate-600 transition hover:bg-slate-50 sm:px-3.5 sm:py-2"
                >
                  <CalendarIcon className="h-4 w-4 text-slate-500" />
                  <span className="hidden sm:inline">{t("company.users.calendar")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setUserDialogOpen(true)}
                  aria-label={t("company.users.new_user")}
                  title={t("company.users.new_user")}
                  className="inline-flex items-center justify-center gap-1.5 rounded-[10px] border border-[#1463C7] bg-[#1463C7] p-2 text-[13.5px] font-bold text-white shadow-[0_1px_2px_rgba(20,99,199,0.3)] transition hover:brightness-110 sm:px-3.5 sm:py-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t("company.users.new_user")}</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-px border-t border-[#EEF1F5] bg-[#EEF1F5]">
              <div className="bg-white px-3 py-2 sm:px-5 sm:py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 sm:text-[11px] sm:tracking-[0.16em]">
                  {t("company.users.stats.total")}
                </div>
                <div className="mt-0.5 text-base font-extrabold tabular-nums text-[#1463C7] sm:mt-1 sm:text-xl">
                  {totalUsers}
                </div>
              </div>
              <div className="bg-white px-3 py-2 sm:px-5 sm:py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 sm:text-[11px] sm:tracking-[0.16em]">
                  {t("company.users.stats.on_page")}
                </div>
                <div className="mt-0.5 text-base font-extrabold tabular-nums text-slate-900 sm:mt-1 sm:text-xl">
                  {onPageCount}
                </div>
              </div>
              <div className="bg-white px-3 py-2 sm:px-5 sm:py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 sm:text-[11px] sm:tracking-[0.16em]">
                  {t("company.users.stats.selected")}
                </div>
                <div className="mt-0.5 text-base font-extrabold tabular-nums text-slate-900 sm:mt-1 sm:text-xl">
                  {selectedItems.length}
                </div>
              </div>
            </div>
          </section>

          {/* Active-filter chips — mobile only (desktop shows the full filter card below). */}
          {hasActiveFilters ? (
            <div className="mb-4 sm:hidden">
              <ModuleFilter
                chipsOnly
                disabled={isLoading}
                onFiltersChange={handleFiltersChange}
                initialFilters={getInitialFilters()}
                customCheckboxButtonFilters={customCheckboxButtonFilters}
                acceptedStatuses={["driver"]}
              />
            </div>
          ) : null}

          {/* Desktop filter card — replaced by the header filter icon + dialog on mobile. */}
          <section className="mb-6 hidden overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)] sm:block">
            <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-5 py-3.5">
              <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
                <FunnelIcon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <h2 className="text-sm font-bold text-slate-900">
                  {t("company.users.filters_heading")}
                </h2>
                <p className="text-xs text-slate-500">
                  {t("company.users.filters_description")}
                </p>
              </div>
            </div>
            <div className="space-y-5 px-5 py-5">
              <ModuleFilter
                className="mt-0"
                disabled={isLoading}
                onFiltersChange={handleFiltersChange}
                initialFilters={getInitialFilters()}
                searchPlaceholder={t("company.users.search_placeholder")}
                filterTitle={t("company.users.filter_title")}
                customCheckboxButtonFilters={customCheckboxButtonFilters}
                acceptedStatuses={["driver"]}
              />
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,28,46,0.05)]">
            <div className="flex items-center gap-2.5 border-b border-[#EEF1F5] px-3 py-3 sm:px-5 sm:py-3.5">
              <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-[#EAF3FD] text-[#1463C7]">
                <UserGroupIcon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <h2 className="text-sm font-bold text-slate-900">
                  {employeesOnly
                    ? t("hr.employees.section.title")
                    : t("company.users.section.title")}
                </h2>
                <p className="hidden text-xs text-slate-500 sm:block">
                  {employeesOnly
                    ? t("hr.employees.section.subtitle")
                    : t("company.users.section.subtitle")}
                </p>
              </div>
              <span className="ml-auto inline-flex items-center rounded-full bg-[#EAF3FD] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#1463C7]">
                {employeesOnly
                  ? t("hr.employees.section.kicker")
                  : t("company.users.section.kicker")}
              </span>
            </div>

            <UsersTable
              onItemsSelect={(items) => setSelectedItems(items)}
              itemState={usersState}
              itemsCount={totalUsers}
              currentCount={onPageCount}
              page={controller.page}
              onPageChange={handlePageChange}
              perPage={controller.per_page}
              onInvitationResent={() => dispatch(getUsersAsync(controller))}
            />
          </section>
        </div>
      </div>

      {/* Mobile filter dialog — opened from the header filter icon button. */}
      <PMDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        title={t("company.users.filter_title")}
        size="md"
        dense
        closeOnOutsideClick
      >
        <ModuleFilter
          panelMode
          disabled={isLoading}
          onFiltersChange={handleFiltersChange}
          initialFilters={getInitialFilters()}
          searchPlaceholder={t("company.users.search_placeholder")}
          filterTitle={t("company.users.filter_title")}
          customCheckboxButtonFilters={customCheckboxButtonFilters}
          acceptedStatuses={["driver"]}
        />
      </PMDialog>

      <PMDialog
        open={userDialogOpen}
        onClose={() => {
          setUserDialogOpen(false);
          setEmailSearchValue("");
          setEmailCheckResult(null);
          setSelectedSupplier(null);
        }}
        title={t("Create New User")}
        size="2xl"
      >
        <Formik
          initialValues={{
            name: "",
            email: "",
            mobile: "",
            mobile_work: "",
            active: true,
            approved: true,
            show_in_dropdown: false,
            user_type_id: 2,
            hired_employee_supplier_id: null,
          }}
          validationSchema={Yup.object({
            name: Yup.string()
              .max(255)
              .when("$emailExists", {
                is: false,
                then: (schema) => schema.required(t("Name is required")),
                otherwise: (schema) => schema,
              }),
            email: Yup.string()
              .email(t("Invalid email address"))
              .required(t("Email is required")),
            mobile: Yup.string(),
            mobile_work: Yup.string(),
          })}
          validationContext={{ emailExists: emailCheckResult?.exists }}
          onSubmit={(values, helpers) => {
            try {
              dispatchWithToast(
                dispatch(createUserAsync(values)),
                (data) => {
                  // Refresh user list after successful creation
                  dispatch(getUsersAsync(controller));
                  setUserDialogOpen(false);
                  helpers.resetForm();
                  setEmailSearchValue("");
                  setEmailCheckResult(null);
                  setSelectedSupplier(null);
                },
                () => {
                  setUserDialogOpen(false);
                }
              );
              helpers.setSubmitting(false);
            } catch (err) {
              console.error(err);
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: err.message });
              helpers.setSubmitting(false);
            }
          }}
        >
          {(formik) => {
            const emailExists = emailCheckResult?.exists;
            const alreadyAssociated = emailCheckResult?.already_associated;
            const hasPendingInvitation = emailCheckResult?.has_pending_invitation;
            const canResendInvitation = emailCheckResult?.can_resend_invitation;
            const existingUser = emailCheckResult?.user;

            return (
              <Form>
                <div className="space-y-6">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                      {t("Search Email")}
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={emailSearchValue}
                        onChange={(e) => handleEmailSearchChange(e.target.value, formik)}
                        placeholder={t("Enter email to search...")}
                        className="w-full rounded-[10px] border border-slate-200 bg-white shadow-sm focus:border-[#1463C7] focus:ring-[#1463C7] sm:text-sm px-3 py-2"
                      />
                      {checkingEmail && (
                        <div className="absolute right-3 top-2.5">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1463C7]"></div>
                        </div>
                      )}
                    </div>
                    {emailCheckResult && emailExists && (
                      <div className="mt-2 p-3 bg-[#EAF3FD] border border-[#BFDCF7] rounded-xl">
                        <div className="flex items-start">
                          <InformationCircleIcon className="h-5 w-5 text-[#1463C7] mt-0.5 mr-2" />
                          <div className="flex-1">
                            <p className="text-sm font-bold text-[#0B3A78]">
                              {t("User already exists")}
                            </p>
                            {existingUser && (
                              <p className="text-sm text-[#1463C7] mt-1">
                                {t("Name")}: {existingUser.name}
                              </p>
                            )}
                            {alreadyAssociated ? (
                              <p className="text-sm text-[#1463C7] mt-1">
                                {t("User is already associated with this company")}
                              </p>
                            ) : canResendInvitation && hasPendingInvitation ? (
                              <p className="text-sm text-[#1463C7] mt-1">
                                {t("An invitation has already been sent to this user")}
                              </p>
                            ) : canResendInvitation ? (
                              <p className="text-sm text-[#1463C7] mt-1">
                                {t("company.users.invitation.expired")}
                              </p>
                            ) : (
                              <p className="text-sm text-[#1463C7] mt-1">
                                {t("An invitation will be sent to this user")}
                              </p>
                            )}
                            {canResendInvitation && !alreadyAssociated && (
                              <button
                                type="button"
                                onClick={handleResendInvitation}
                                disabled={resendingInvitation}
                                className="mt-3 inline-flex items-center rounded-[10px] border border-[#1463C7] bg-[#1463C7] px-3.5 py-1.5 text-xs font-bold text-white shadow-[0_1px_2px_rgba(20,99,199,0.3)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {resendingInvitation
                                  ? t("company.users.invitation.resending")
                                  : t("company.users.invitation.resend_button")}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {!emailExists && (
                    <>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <PMField
                            name="name"
                            type="text"
                            label={t("Name")}
                            required
                            {...formik}
                          />
                        </div>

                        <div className="sm:col-span-2">
                          <PMField
                            name="email"
                            type="email"
                            label={t("Email")}
                            required
                            disabled={emailSearchValue !== ""}
                            {...formik}
                          />
                        </div>

                        <div>
                          <PMField
                            name="mobile"
                            type="text"
                            label={t("Mobile")}
                            {...formik}
                          />
                        </div>

                        <div>
                          <PMField
                            name="mobile_work"
                            type="text"
                            label={t("Work Mobile")}
                            {...formik}
                          />
                        </div>

                        <div className="sm:col-span-2">
                          <div className="grid grid-cols-2 gap-4">
                            <PMSwitch
                              label={t("Active")}
                              name="active"
                              {...formik}
                            />
                            <PMSwitch
                              label={t("Show in dropdown")}
                              name="show_in_dropdown"
                              {...formik}
                            />
                          </div>
                        </div>

                        <div className="sm:col-span-2">
                          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                            {t("company.users.user_type.label")}
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            {[
                              { id: 2, label: t("company.users.user_type.employee") },
                              { id: 3, label: t("company.users.user_type.hired") },
                            ].map((opt) => {
                              const isActive = formik.values.user_type_id === opt.id;
                              return (
                                <button
                                  key={opt.id}
                                  type="button"
                                  onClick={() => {
                                    formik.setFieldValue("user_type_id", opt.id);
                                    if (opt.id !== 3) {
                                      formik.setFieldValue("hired_employee_supplier_id", null);
                                      setSelectedSupplier(null);
                                    }
                                  }}
                                  className={`rounded-[10px] border px-3 py-2 text-sm font-semibold transition-colors ${
                                    isActive
                                      ? "border-[#1463C7] bg-[#EAF3FD] text-[#0B3A78]"
                                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {formik.values.user_type_id === 3 && (
                          <div className="sm:col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                              {t("company.users.user_type.supplier")}
                            </label>
                            <button
                              type="button"
                              onClick={() => setSupplierSelectorOpen(true)}
                              className="w-full flex items-center justify-between rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-slate-300"
                            >
                              <span className="truncate">
                                {selectedSupplier
                                  ? selectedSupplier.displayTitle || selectedSupplier.name
                                  : t("company.users.user_type.select_supplier")}
                              </span>
                            </button>
                            {formik.touched.hired_employee_supplier_id &&
                              !formik.values.hired_employee_supplier_id && (
                                <p className="mt-1 text-xs text-[#DC2626]">
                                  {t("company.users.user_type.supplier_required")}
                                </p>
                              )}
                            <PMDialog
                              open={supplierSelectorOpen}
                              onClose={() => setSupplierSelectorOpen(false)}
                              size="md"
                              dense
                              zIndex={2400}
                            >
                              <div className="h-[70vh]">
                                <ModernSupplierSelector
                                  open={supplierSelectorOpen}
                                  selectedSuppliers={
                                    selectedSupplier ? [selectedSupplier] : []
                                  }
                                  onChange={(suppliers) => {
                                    const s = suppliers?.[0] || null;
                                    setSelectedSupplier(s);
                                    formik.setFieldValue(
                                      "hired_employee_supplier_id",
                                      s ? s.id : null
                                    );
                                  }}
                                  onClose={() => setSupplierSelectorOpen(false)}
                                />
                              </div>
                            </PMDialog>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-[#EEF1F5]">
                  <button
                    type="button"
                    onClick={() => {
                      setUserDialogOpen(false);
                      formik.resetForm();
                      setSelectedSupplier(null);
                    }}
                    className="rounded-[10px] border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1463C7]"
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className="rounded-[10px] border border-[#1463C7] bg-[#1463C7] px-4 py-2 text-sm font-bold text-white shadow-[0_1px_2px_rgba(20,99,199,0.3)] transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1463C7] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                  >
                    {formik.isSubmitting ? t("Creating...") : t("Create User")}
                  </button>
                </div>
              </div>
            </Form>
            );
          }}
        </Formik>
      </PMDialog>
    </>
  );
};
